import * as React from "react";
import { cn } from "../lib/utils.js";
import {
  CONTACT_STATUS_COLOR_KEY,
  LEAD_STATUS_COLOR_KEY,
  type CalendarEventTone,
} from "./calendar/calendar.types.js";

export type StatusBadgeVariant =
  | "default"
  | "success"
  | "warning"
  | "secondary"
  | "destructive"
  | "outline";

export type OperationalStatusDomain =
  | "general"
  | "calendar_connection"
  | "channel"
  | "dealership"
  | "integration"
  | "integration_delivery"
  | "inventory"
  | "outreach"
  | "sftp"
  | "telephony"
  | "user"
  | "webhook_delivery";

const OPERATIONAL_STATUS_VARIANTS: Record<
  OperationalStatusDomain,
  Readonly<Record<string, StatusBadgeVariant>>
> = {
  general: {
    active: "success",
    available: "success",
    completed: "success",
    connected: "success",
    configured: "success",
    connecting: "warning",
    healthy: "success",
    sent: "success",
    synced: "success",
    approved: "success",
    verified: "success",
    onboarding: "warning",
    pilot: "warning",
    pending: "warning",
    intransit: "warning",
    rotating: "warning",
    backfilling: "warning",
    blocked_compliance: "warning",
    needs_setup: "warning",
    failing: "destructive",
    failed: "destructive",
    errored: "destructive",
    bounced: "destructive",
    error: "destructive",
    paused: "secondary",
    inactive: "secondary",
    disabled: "secondary",
    sold: "secondary",
    draft: "secondary",
    released: "secondary",
    not_applicable: "secondary",
  },
  calendar_connection: {
    active: "secondary",
    errored: "destructive",
    error: "destructive",
    revoked: "destructive",
  },
  channel: {
    draft: "secondary",
    provisioning: "default",
    active: "success",
    paused: "warning",
    failing: "destructive",
    released: "secondary",
    blocked_compliance: "warning",
  },
  dealership: {
    onboarding: "warning",
    pilot: "warning",
    active: "success",
    paused: "secondary",
    inactive: "secondary",
  },
  integration: {
    synced: "success",
    syncing: "default",
    backfilling: "warning",
    errored: "destructive",
  },
  integration_delivery: {
    sent: "success",
    failed: "warning",
    bounced: "destructive",
  },
  inventory: {
    available: "success",
    intransit: "warning",
    pending: "warning",
    sold: "secondary",
  },
  outreach: {
    new: "secondary",
    ready_to_reach_out: "secondary",
    to_follow_up: "default",
    interested: "default",
    demo_scheduled: "warning",
    negotiating: "warning",
    won: "success",
    lost: "secondary",
  },
  sftp: {
    active: "success",
    disabled: "secondary",
    rotating: "warning",
  },
  telephony: {
    not_started: "secondary",
    draft: "secondary",
    pending: "warning",
    pending_otp: "warning",
    in_review: "warning",
    in_progress: "warning",
    approved: "success",
    verified: "success",
    failed: "destructive",
  },
  user: {
    active: "success",
    pending: "warning",
    inactive: "secondary",
  },
  webhook_delivery: {
    pending: "outline",
    delivered: "secondary",
    failed: "destructive",
  },
};

/**
 * Shared operational status → Badge variant resolver.
 *
 * Domain overrides retain intentional differences such as a paused channel
 * requiring attention while a paused dealership is simply inactive. Unknown
 * values degrade to a quiet secondary badge instead of gaining an action color.
 */
export function operationalStatusVariant(
  status: string,
  domain: OperationalStatusDomain = "general",
): StatusBadgeVariant {
  const normalizedStatus = status.trim().toLowerCase().replace(/[\s-]+/g, "_");
  return (
    OPERATIONAL_STATUS_VARIANTS[domain][normalizedStatus] ??
    OPERATIONAL_STATUS_VARIANTS.general[normalizedStatus] ??
    "secondary"
  );
}

/** Shared CalendarEventTone → Badge variant bridge. */
export function toneToBadgeVariant(
  tone: CalendarEventTone | undefined,
): StatusBadgeVariant {
  if (tone === "danger") return "destructive";
  if (tone === "success") return "success";
  if (tone === "warning") return "warning";
  if (tone === "muted") return "secondary";
  return "default";
}

/** Contact lifecycle status → Badge variant, derived from the shared tone map. */
export function contactStatusVariant(status: string): StatusBadgeVariant {
  return toneToBadgeVariant(CONTACT_STATUS_COLOR_KEY[status] ?? "muted");
}

export interface StatusPillProps {
  /** Lead status string — "new" | "working" | "appointment" | "won" | "lost". String type for forward-compat. */
  status: string;
  /** Display label — resolved by the caller (e.g. via leadStatusLabel()). Defaults to the raw status. */
  label?: string;
  /** sm for dense table rows, md (default) for board headers / cards. */
  size?: "sm" | "md";
  className?: string;
}

/**
 * Static class pairs per CalendarEventTone.
 * Tailwind-JIT requires complete string literals — no dynamic interpolation.
 * WHY here and not inline: Tailwind purges dynamic class fragments, making
 * color invisible in production if built with template strings.
 */
const TONE_CLASSES: Record<CalendarEventTone, string> = {
  default: "bg-primary/15 text-primary",
  success: "bg-[hsl(var(--success)/0.15)] text-[hsl(var(--success))]",
  warning: "bg-[hsl(var(--triage-warm-bg))] text-[hsl(var(--triage-warm-fg))]",
  danger: "bg-[hsl(var(--triage-hot-bg))] text-[hsl(var(--triage-hot))]",
  muted: "bg-muted text-muted-foreground",
};

const TONE_DOT_CLASSES: Record<CalendarEventTone, string> = {
  default: "bg-primary",
  success: "bg-success",
  warning: "border border-foreground/40 bg-warning",
  danger: "bg-destructive",
  muted: "bg-muted-foreground/50",
};

const TONE_CSS_COLORS: Record<CalendarEventTone, string> = {
  default: "hsl(var(--primary))",
  success: "hsl(var(--success))",
  warning: "hsl(var(--warning))",
  danger: "hsl(var(--destructive))",
  muted: "hsl(var(--muted-foreground))",
};

/** CSS color token for charts that cannot consume semantic utility classes. */
export function toneCssColor(tone: CalendarEventTone | undefined): string {
  return TONE_CSS_COLORS[tone ?? "muted"];
}

export interface TonePillProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: CalendarEventTone;
  size?: "sm" | "md";
}

/** Soft semantic pill for any domain status already resolved to a tone. */
export function TonePill({
  tone = "muted",
  size = "md",
  className,
  ...props
}: TonePillProps): React.JSX.Element {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full font-medium leading-none",
        size === "sm" ? "px-1.5 py-0.5 text-xs" : "px-2 py-1 text-xs",
        TONE_CLASSES[tone],
        className,
      )}
      {...props}
    />
  );
}

export interface ToneDotProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: CalendarEventTone;
}

/** Compact semantic marker for legends and dense directory rows. */
export function ToneDot({
  tone = "muted",
  className,
  ...props
}: ToneDotProps): React.JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={cn("h-2 w-2 shrink-0 rounded-full", TONE_DOT_CLASSES[tone], className)}
      {...props}
    />
  );
}

/**
 * StatusPill — a soft color-coded pill for lead pipeline status.
 *
 * Color source: LEAD_STATUS_COLOR_KEY (calendar.types.ts) → CalendarEventTone token.
 * Single source of status→color truth for the entire app (board, contacts, 360, calendar).
 * Unknown statuses fall back to muted rather than crashing or going blank.
 */
export function StatusPill({ status, label, size = "md", className }: StatusPillProps) {
  const tone: CalendarEventTone = LEAD_STATUS_COLOR_KEY[status] ?? "muted";
  const displayLabel = label ?? status;

  return (
    <TonePill tone={tone} size={size} className={className}>
      {displayLabel}
    </TonePill>
  );
}

/** Shared lead-status marker for board headers and compact legends. */
export function StatusDot({
  status,
  tone,
  className,
}: {
  status?: string;
  tone?: CalendarEventTone;
  className?: string;
}): React.JSX.Element {
  const resolvedTone = tone ?? (status ? LEAD_STATUS_COLOR_KEY[status] : undefined) ?? "muted";
  return <ToneDot tone={resolvedTone} className={className} />;
}
